import { SearchspringSearchProvider } from 'libs/searchspring/fetchers/search'

import clsx from 'clsx'

import { useEffect, useRef, useState } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useMerchedResults } from 'libs/searchspring/hooks/useMerchedResults'
import { useOption } from 'libs/searchspring/hooks/useOption'
import { FiltersProvider } from 'libs/searchspring/hooks/useFilters'
import { CollectionToolbar } from 'libs/searchspring/components/collection/CollectionToolbar'
import { FilterDrawer } from 'libs/searchspring/components/collection/filters/FilterDrawer'
import { Pagination } from 'libs/searchspring/components/collection/Pagination'
import { SearchHeader } from 'libs/searchspring/components/SearchHeader'
import { ResultItem } from 'libs/searchspring/components/ResultCardWrapper'
import { calculateProductCount } from 'libs/searchspring/components/collection/filters/ProductCount'

const GRID_IMAGE_SIZES = '(min-width: 1828px) 25vw, (min-width: 768px) 33vw, 100vw'
const GRID_IMAGE_WIDTHS = [200, 320, 480, 640, 800, 1000, 1200, 1440]

function SearchGrid() {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const hasStartedLoading = useRef(false)
  const { results, isLoading } = useSearchspringContext()
  const currentResults = useMerchedResults()
  const gridChange = results?.pagination?.currentPage
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const { setScrollElement } = useFilterScroll()
  const query = useOption('query', { syncWithURL: true })

  const { corrected = '', original = '' } = results?.query || {}
  const didYouMean = results?.didYouMean?.query || ''

  const corrections = {
    corrected,
    original,
    didYouMean
  }

  useEffect(() => {
    if (isLoading) hasStartedLoading.current = true
    if (!isLoading && hasStartedLoading.current) setFirstLoad(false)
  }, [isLoading])

  useScrollCache([currentResults, firstLoad])

  const totalResults = calculateProductCount(results)

  return (
    <div className="flex grow flex-col">
      <SearchHeader
        query={query}
        totalResults={totalResults}
        isLoading={isLoading}
        corrections={corrections}
        results={results}
      />
      <FiltersProvider>
        {currentResults.length > 0 && (
          <>
            <CollectionToolbar />
            <div className="flex grow flex-col md:grid md:grid-cols-min-full">
              <FilterDrawer />
              <div className="flex grow flex-col">
                <div
                  ref={(el) => {
                    setScrollElement(el)
                    setEntry(el)
                  }}
                  className={clsx(
                    'grid w-full grid-cols-2 gap-x-gutter gap-y-2 pb-3.5 [grid-auto-flow:row_dense] md:grid-cols-3 lg:grid-cols-4 xxxl:grid-cols-5 image-view-large:grid-cols-1 image-view-large:md:grid-cols-2 image-view-large:lg:grid-cols-3 image-view-large:xxxl:grid-cols-4',
                    isLoading && firstLoad && 'min-h-screen-height'
                  )}
                >
                  {currentResults?.map((item, index) => {
                    return (
                      <ResultItem item={item} key={item.handle ?? `item-${index}`} className="wk-wishlist-button-target" dataIndex={index.toString()} eager={index === 0} imageSizes={GRID_IMAGE_SIZES} imageWidths={GRID_IMAGE_WIDTHS} />
                    )
                  })}
                </div>
                {!firstLoad && <Pagination />}
              </div>
            </div>
          </>
        )}
      </FiltersProvider>
    </div>
  )
}

export function WrappedSearchGrid({ perPage }) {
  scrollHistoryManager.updateHistoryState()
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('query')
  const resultsPerPage = perPage || window.settings?.per_page
  return (
    <SearchspringSearchProvider query={queryParam} resultsPerPage={resultsPerPage}>
      <SearchGrid />
    </SearchspringSearchProvider>
  )
}
